/**
 * WeaveIn Verify — H.R. 1 community-engagement hours, captured where they happen.
 *
 * One page, five tabs, in the order the work happens:
 *   Overview  → where things stand this month
 *   Registry  → qualify an organization once; every Verify org reads it
 *   Capture   → staff entry, or a photographed paper sign-in sheet a person confirms
 *   Records   → confirm QR check-ins, answer disputes, void mistakes
 *   Medicaid  → match lines to enrollments, deliver a month into the Medicaid
 *               work record, and see redetermination readiness
 *
 * Gated on the `hr1_verify` capability in app.tsx; the API 403s on its own too.
 */

import React, { useState } from 'react';
import { PageHeader, PageContainer } from '../weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { VerifyOverview } from './VerifyOverview';
import { VerifyRegistry } from './VerifyRegistry';
import { VerifyCapture } from './VerifyCapture';
import { VerifyRecords } from './VerifyRecords';
import { VerifyMedicaid } from './VerifyMedicaid';

type TabKey = 'overview' | 'registry' | 'capture' | 'records' | 'medicaid';

const TAB_STORAGE_KEY = 'verify.activeTab';

function readTab(): TabKey {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY);
    if (v === 'overview' || v === 'registry' || v === 'capture' || v === 'records' || v === 'medicaid') return v;
  } catch {
    // private mode
  }
  return 'overview';
}

export default function VerifyPage() {
  const { t } = useAppTranslation();
  const [tab, setTab] = useState<TabKey>(readTab);

  const go = (next: string) => {
    setTab(next as TabKey);
    try {
      localStorage.setItem(TAB_STORAGE_KEY, next);
    } catch {
      // private mode
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-paper">
      <PageHeader
        eyebrow={t('verify.eyebrow', 'Medicaid · H.R. 1')}
        title={t('verify.title', 'Verify')}
        description={t(
          'verify.description',
          'Community-engagement hours recorded where they happen, confirmed by a person, and delivered into Medicaid enrollment and redetermination.',
        )}
        tabs={[
          { key: 'overview', label: t('verify.tab.overview', 'Overview') },
          { key: 'registry', label: t('verify.tab.registry', 'Registry') },
          { key: 'capture', label: t('verify.tab.capture', 'Capture') },
          { key: 'records', label: t('verify.tab.records', 'Records') },
          { key: 'medicaid', label: t('verify.tab.medicaid', 'Medicaid') },
        ]}
        activeTab={tab}
        onTabChange={go}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <PageContainer size="wide">
          {tab === 'overview' && <VerifyOverview onNavigate={go} />}
          {tab === 'registry' && <VerifyRegistry />}
          {tab === 'capture' && <VerifyCapture onNavigate={go} />}
          {tab === 'records' && <VerifyRecords />}
          {tab === 'medicaid' && <VerifyMedicaid />}
        </PageContainer>
      </div>
    </div>
  );
}
